import React, { useState, useEffect, useCallback } from 'react';
import './AdminPanel.css';
import PageControlLogin from './pageControlLogin';

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [config, setConfig] = useState(null);
  const [testimonialsConfig, setTestimonialsConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testimonialsSaving, setTestimonialsSaving] = useState(false);
  
  // Banner animation controls
  const [scrollDuration, setScrollDuration] = useState(75);
  const [startPosition, setStartPosition] = useState(19);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://yuccan.tech';

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('admin_token');
      const expires = localStorage.getItem('admin_expires');
      
      if (!token || !expires || Date.now() > parseInt(expires)) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_expires');
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          setAuthToken(token);
          setIsAuthenticated(true);
        } else {
          // Clear invalid session data
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_expires');
          setAuthToken(null);
          setIsAuthenticated(false);
        }
      } catch (err) {
        console.error('Auth verification failed:', err);
        // Clear session data on network/server error
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_expires');
        setAuthToken(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };
    
    checkAuth();
  }, [API_BASE_URL]);

  // Handle login
  const handleLogin = (token) => {
    setAuthToken(token);
    setIsAuthenticated(true);
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_expires');
      setAuthToken(null);
      setIsAuthenticated(false);
      setConfig(null);
      setTestimonialsConfig(null);
    }
  };

  // Fetch current configuration
  const fetchConfig = useCallback(async () => {
    if (!authToken) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Fetch both promo and testimonials config
      const [promoResponse, testimonialsResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/static-config/promo`, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        }),
        fetch(`${API_BASE_URL}/api/static-config/testimonials`, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        })
      ]);
      
      // Check for 401 responses (session expired)
      if (promoResponse.status === 401 || testimonialsResponse.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_expires');
        setAuthToken(null);
        setIsAuthenticated(false);
        setError('Session expired. Please login again.');
        return;
      }
      
      const promoResult = await promoResponse.json();
      const testimonialsResult = await testimonialsResponse.json();
      
      if (promoResponse.ok && promoResult.success) {
        setConfig({
          ...promoResult.data,
          bannerItems: [] // Static config doesn't have banner items
        });
      } else {
        setError(promoResult.message || `Promo config error: ${promoResponse.status}`);
      }
      
      if (testimonialsResponse.ok && testimonialsResult.success) {
        setTestimonialsConfig(testimonialsResult.data);
      } else {
        setError(testimonialsResult.message || `Testimonials config error: ${testimonialsResponse.status}`);
      }
    } catch (err) {
      setError(`Connection error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [authToken, API_BASE_URL]);


  // Toggle promotions on/off
  const togglePromotions = async () => {
    if (!config) return;
    
    try {
      setSaving(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/api/static-config/promo`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ enabled: !config.enabled })
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        // Refresh the config to get the current state from the file
        await fetchConfig();
      } else if (response.status === 401) {
        // Session expired, clear auth and redirect to login
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_expires');
        setAuthToken(null);
        setIsAuthenticated(false);
        setError('Session expired. Please login again.');
      } else {
        setError(result.message || `HTTP Error: ${response.status}`);
      }
    } catch (err) {
      setError(`Update failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Toggle testimonials on/off
  const toggleTestimonials = async () => {
    if (!testimonialsConfig) return;
    
    try {
      setTestimonialsSaving(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/api/static-config/testimonials`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ enabled: !testimonialsConfig.enabled })
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        // Refresh the config to get the current state from the file
        await fetchConfig();
      } else if (response.status === 401) {
        // Session expired, clear auth and redirect to login
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_expires');
        setAuthToken(null);
        setIsAuthenticated(false);
        setError('Session expired. Please login again.');
      } else {
        setError(result.message || `HTTP Error: ${response.status}`);
      }
    } catch (err) {
      setError(`Update failed: ${err.message}`);
    } finally {
      setTestimonialsSaving(false);
    }
  };

  // Load banner animation settings from localStorage or CSS
  const loadBannerSettings = useCallback(() => {
    // Load from localStorage first
    const savedDuration = localStorage.getItem('bannerScrollDuration');
    const savedPosition = localStorage.getItem('bannerStartPosition');
    
    if (savedDuration) {
      setScrollDuration(parseInt(savedDuration));
    }
    if (savedPosition) {
      setStartPosition(parseInt(savedPosition));
    }
    
    // Apply to CSS
    const root = document.documentElement;
    root.style.setProperty('--banner-scroll-duration', `${savedDuration || 75}s`);
    root.style.setProperty('--banner-start-position', `${savedPosition || 19}%`);
  }, []);

  // Apply banner animation changes
  const applyBannerChanges = () => {
    const root = document.documentElement;
    const scrollingContent = document.querySelector('.scrolling-content');
    
    // Temporarily pause animation to force restart
    if (scrollingContent) {
      scrollingContent.style.animationPlayState = 'paused';
    }
    
    // Update CSS variables
    root.style.setProperty('--banner-scroll-duration', `${scrollDuration}s`);
    root.style.setProperty('--banner-start-position', `${startPosition}%`);
    
    // Force animation restart by temporarily removing and re-adding the animation
    if (scrollingContent) {
      setTimeout(() => {
        scrollingContent.style.animation = 'none';
        setTimeout(() => {
          scrollingContent.style.animation = `scroll ${scrollDuration}s linear infinite`;
          scrollingContent.style.animationPlayState = 'running';
        }, 10);
      }, 10);
    }
    
    // Save to localStorage for persistence
    localStorage.setItem('bannerScrollDuration', scrollDuration);
    localStorage.setItem('bannerStartPosition', startPosition);
    
    // Trigger custom event to notify banner component
    window.dispatchEvent(new CustomEvent('bannerAnimationChange'));
    
    setError('✅ Banner animation settings updated successfully!');
    setTimeout(() => setError(null), 3000);
  };

  // Reset banner settings to defaults
  const resetBannerSettings = () => {
    setScrollDuration(75);
    setStartPosition(19);
    
    const root = document.documentElement;
    const scrollingContent = document.querySelector('.scrolling-content');
    
    // Temporarily pause animation to force restart
    if (scrollingContent) {
      scrollingContent.style.animationPlayState = 'paused';
    }
    
    // Update CSS variables
    root.style.setProperty('--banner-scroll-duration', '75s');
    root.style.setProperty('--banner-start-position', '19%');
    
    // Force animation restart
    if (scrollingContent) {
      setTimeout(() => {
        scrollingContent.style.animation = 'none';
        setTimeout(() => {
          scrollingContent.style.animation = 'scroll 75s linear infinite';
          scrollingContent.style.animationPlayState = 'running';
        }, 10);
      }, 10);
    }
    
    localStorage.removeItem('bannerScrollDuration');
    localStorage.removeItem('bannerStartPosition');
    
    // Trigger custom event to notify banner component
    window.dispatchEvent(new CustomEvent('bannerAnimationChange'));
    
    setError('✅ Banner settings reset to defaults!');
    setTimeout(() => setError(null), 3000);
  };

  useEffect(() => {
    if (isAuthenticated && authToken) {
      fetchConfig();
    }
  }, [isAuthenticated, authToken, fetchConfig]);

  // Load banner settings when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadBannerSettings();
    }
  }, [isAuthenticated, loadBannerSettings]);

  if (loading) {
    return (
      <div className="admin-panel">
        <div className="loading">🔄 Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <PageControlLogin onLogin={handleLogin} />;
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h1>Yuccan Tech Page Control Panel</h1>
        <p>Manage promotional banners and testimonials</p>
        <button onClick={handleLogout} className="logout-btn">
          🚪 Logout
        </button>
      </div>

      {error && (
        <div className="error-message">
          ❌ {error}
          <button onClick={fetchConfig} className="retry-btn">Retry</button>
        </div>
      )}

      {config && testimonialsConfig && (
        <div className="admin-content">
          
          {/* Promotions Status and Toggle */}
          <div className="status-section">
            <div className="status-display">
              <span className={`status-indicator ${config.enabled ? 'enabled' : 'disabled'}`}>
                {config.enabled ? '✅ Promotions ENABLED' : '❌ Promotions DISABLED'}
              </span>
              <span className="last-modified">
                Last updated: {new Date(config.lastModified).toLocaleString()}
              </span>
            </div>
            
            <button 
              onClick={togglePromotions}
              disabled={saving}
              className={`toggle-btn ${config.enabled ? 'enabled' : 'disabled'}`}
            >
              {saving ? '🔄 Updating...' : (config.enabled ? 'Disable Promotions' : 'Enable Promotions')}
            </button>
          </div>

          {/* Testimonials Status and Toggle */}
          <div className="status-section testimonials-section">
            <div className="status-display">
              <span className={`status-indicator ${testimonialsConfig.enabled ? 'enabled' : 'disabled'}`}>
                {testimonialsConfig.enabled ? '✅ Testimonials ENABLED' : '❌ Testimonials DISABLED'}
              </span>
              <span className="last-modified">
                Last updated: {new Date(testimonialsConfig.lastModified).toLocaleString()}
              </span>
              <span className="compliance-note">
                📋 For data compliance & client privacy
              </span>
            </div>
            
            <button 
              onClick={toggleTestimonials}
              disabled={testimonialsSaving}
              className={`toggle-btn ${testimonialsConfig.enabled ? 'enabled' : 'disabled'}`}
            >
              {testimonialsSaving ? '🔄 Updating...' : (testimonialsConfig.enabled ? 'Disable Testimonials' : 'Enable Testimonials')}
            </button>
          </div>

          {/* Banner Animation Controls */}
          <div className="banner-animation-section">
            <h3>🎬 Banner Animation Controls</h3>
            <div className="banner-controls">
              <div className="control-group">
                <label htmlFor="scroll-duration">
                  <strong>Scroll Speed (seconds):</strong>
                  <span className="control-hint">Lower = faster scrolling</span>
                </label>
                <div className="input-with-current">
                  <input
                    id="scroll-duration"
                    type="number"
                    min="5"
                    max="200"
                    step="5"
                    value={scrollDuration}
                    onChange={(e) => setScrollDuration(parseInt(e.target.value) || 75)}
                    className="number-input"
                  />
                  <span className="current-value">Current: {scrollDuration}s</span>
                </div>
              </div>

              <div className="control-group">
                <label htmlFor="start-position">
                  <strong>Gap Size (%):</strong>
                  <span className="control-hint">Higher = bigger gap between cycles</span>
                </label>
                <div className="input-with-current">
                  <input
                    id="start-position"
                    type="number"
                    min="0"
                    max="50"
                    step="1"
                    value={startPosition}
                    onChange={(e) => setStartPosition(parseInt(e.target.value) || 19)}
                    className="number-input"
                  />
                  <span className="current-value">Current: {startPosition}%</span>
                </div>
              </div>

              <div className="banner-action-buttons">
                <button 
                  onClick={applyBannerChanges}
                  className="apply-btn"
                >
                  🎯 Apply Changes
                </button>
                <button 
                  onClick={resetBannerSettings}
                  className="reset-btn"
                >
                  🔄 Reset to Defaults
                </button>
              </div>
            </div>
          </div>

          {/* Static Configuration Info */}
          <div className="static-info">
            <h3>📄 Static Configuration</h3>
            <div className="info-box">
              <p><strong>Configuration Mode:</strong> Static File</p>
              <p><strong>File Location:</strong> <code>src/config/promoConfig.js</code></p>
              <p><strong>Promotions Setting:</strong> <code>PROMO_FEATURES_ENABLED = {config.enabled ? 'true' : 'false'}</code></p>
              <p><strong>Testimonials Setting:</strong> <code>TESTIMONIALS_ENABLED = {testimonialsConfig.enabled ? 'true' : 'false'}</code></p>
              <div className="info-note">
                <p>ℹ️ This panel controls static feature toggles for compliance and content management.</p>
                <p>• <strong>Promotions:</strong> Controls promotional banner and promos section</p>
                <p>• <strong>Testimonials:</strong> Controls testimonials section for data compliance</p>
                <p>• <strong>Banner Animation:</strong> Controls scroll speed and gap spacing</p>
                <p>• All changes are written directly to the config file or localStorage</p>
                <p>• Components automatically detect changes via API polling</p>
              </div>
            </div>
          </div>

          {/* System Status */}
          <div className="api-info">
            <h3>🔗 System Information</h3>
            <div className="api-details">
              <p><strong>Backend Endpoint:</strong> <code>{API_BASE_URL}/api/static-config/promo</code></p>
              <p><strong>Status:</strong> <span className="status-ok">✅ Connected</span></p>
              <button onClick={fetchConfig} disabled={loading}>🔄 Refresh Status</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default AdminPanel;